import { useQuery } from "@apollo/client";
import { ONE_SECOND_OF_MS } from "reports/utils/timeUtils";

import { ReportParams, TimeRangeFinite } from "../../types";
import {
  PnlBreakdown,
  PnlBreakdownFiltersEnum,
  ASKR_PNL_BREAKDOWN_QUERY,
} from "./pnlBreakdownTypes";
import { askrClients } from "providers/AskrProvider";
import { useParams } from "react-router";

type PnlBreakdownGetterProps = {
  range: TimeRangeFinite;
  machemetryIds?: string[];
  shouldSkipQuery?: boolean;
  setPnlBreakdownData: any;
  /** string filter PnlBreakdown by, possible values are those in PNL_BREAKDOWN_FILTERS */
  filter?: PnlBreakdownFiltersEnum;
  /** whether to filter by machemetry ids or not, can be used at the same time as "filter" */
  shouldFilterByMachemetryId?: boolean;
  /** when filtering by sku, this will filter out PnlBreakdown objects in which the sku is null */
  excludeNullFilterValue?: boolean;
  multiData?: boolean; // sometimes an array of metrics getters are used
  multiDataKey?: string;
};

// tslint:disable-next-line: cognitive-complexity
export default function AskrPnlBreakdownGetter({
  range,
  machemetryIds = [],
  shouldSkipQuery,
  setPnlBreakdownData,
  filter,
  shouldFilterByMachemetryId = false,
  excludeNullFilterValue = false,
  multiData = false,
  multiDataKey,
}: PnlBreakdownGetterProps) {
  const { ckey } = useParams() as ReportParams;
  const { loading, error, refetch } = useQuery(ASKR_PNL_BREAKDOWN_QUERY, {
    skip: shouldSkipQuery || machemetryIds.length <= 0,
    client: askrClients.get(ckey),
    variables: {
      ge: String(range.ge),
      lt: String(range.lt),
      cKey: ckey,
      machemetryIds,
      filter: filter ?? null,
      shouldFilterByMachemetryId,
    },
    notifyOnNetworkStatusChange: true,
    onError: err => {
      // refetch after a 10 second delay so we don't run through all of our servers memmory
      setTimeout(refetch, 10 * ONE_SECOND_OF_MS);
      console.log(
        `Askr PnlBreakdown query gone wrong. cKey: ${ckey}, ge: ${range?.ge}, lt: ${range.lt}, machemetryIds: ${machemetryIds}`
      );
      console.log(`retrying in 10 seconds`);
    },
    onCompleted: async data => {
      if (!shouldSkipQuery && data && !loading && !error) {
        // If there is a filter string, or shouldFilterByMachemetryId, we have requested our data to be filtered
        const dataIsFiltered = filter || shouldFilterByMachemetryId;

        const pnlBreakdownData = data?.pnlBreakdown
          ? dataIsFiltered
            ? data?.pnlBreakdown
            : data.pnlBreakdown[0]
          : null;

        const cleanedPnlBreakdown = excludeNullFilterValue
          ? getPnlBreakdownWithoutNullFilterValue(pnlBreakdownData, filter)
          : pnlBreakdownData;

        // If there are too many machines and it is a multidata query, we setState too many times in the range
        // Setting delays alleviates that issue as a temporary workaround
        if (multiData) {
          await new Promise(r => setTimeout(r, Math.random() * 5000));
          setPnlBreakdownData(currentData => {
            return cleanedPnlBreakdown !== undefined
              ? [
                  ...currentData,
                  // If we need multidata filtered by shift
                  // (Eg. we need to get the data for each process group filtered by shift)
                  multiDataKey && filter == PnlBreakdownFiltersEnum.shiftName
                    ? { pnlBreakdowns: cleanedPnlBreakdown, multiDataKey }
                    : multiDataKey
                      ? { ...cleanedPnlBreakdown, multiDataKey }
                      : cleanedPnlBreakdown,
                ]
              : currentData;
          });
        } else {
          setPnlBreakdownData(cleanedPnlBreakdown);
        }
      }
    },
  });

  return null;
}
/**
 * If filter value of pnlbreakdown is null, filter out of list
 *
 * @param {PnlBreakdown[]} pnlBreakdowns
 * @param {string} filter
 * @return {*}
 */
function getPnlBreakdownWithoutNullFilterValue(
  pnlBreakdowns: PnlBreakdown[],
  filter?: string
) {
  if (!filter) {
    return pnlBreakdowns;
  } else {
    return pnlBreakdowns.filter(pnlBreakdown => pnlBreakdown[filter] !== null);
  }
}
